import React, { useState, useEffect } from 'react';
import { legalApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Input, Spinner, Badge, Tabs } from '../components/UI';
import { Shield, PencilSimple, Clock, CheckCircle, User } from '@phosphor-icons/react';

export default function LegalAdminPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('pages');
  const [pages, setPages] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingType, setEditingType] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [pagesRes, historyRes] = await Promise.all([
        legalApi.getPages(),
        legalApi.getAcceptanceHistory().catch(() => ({ data: [] })),
      ]);
      setPages(pagesRes.data);
      setHistory(historyRes.data);
    } catch {
    } finally { setLoading(false); }
  };

  const openEdit = (p) => {
    setEditingType(p.type);
    setEditTitle(p.title);
    setEditContent(p.content);
  };

  const handleSave = async () => {
    setSaving(true); setSuccess('');
    try {
      await legalApi.updatePage(editingType, { title: editTitle, content: editContent });
      setEditingType(null);
      setSuccess('Legal page updated. Users will be asked to re-accept.');
      setTimeout(() => setSuccess(''), 4000);
      loadData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4" data-testid="legal-admin-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1A1F36]">Legal & Compliance</h1>
        <p className="text-sm text-[#888]">Manage legal pages, track user acceptance</p>
      </div>

      {success && <div className="bg-[#059669]/10 text-[#059669] px-4 py-2 rounded-lg text-sm font-medium" data-testid="legal-success">{success}</div>}

      <Tabs
        tabs={[
          { id: 'pages', label: `Legal Pages (${pages.length})` },
          { id: 'history', label: `Acceptance History (${history.length})` },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      {activeTab === 'pages' && !editingType && (
        <div className="space-y-3">
          {pages.map(p => (
            <Card key={p.type} className="p-4" data-testid={`legal-card-${p.type}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#6B7280]" />
                    <span className="font-semibold text-sm">{p.title}</span>
                    <Badge variant="default">v{p.version}</Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-[#888]">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {p.updated_at ? new Date(p.updated_at).toLocaleDateString('en-US') : '-'}
                    </span>
                    <span className="capitalize text-[#999]">{p.type.replace('_', ' ')}</span>
                  </div>
                  <p className="text-xs text-[#666] mt-2 line-clamp-2">{p.content?.substring(0, 200)}...</p>
                </div>
                <Button size="sm" variant="secondary" onClick={() => openEdit(p)} data-testid={`edit-${p.type}`}>
                  <PencilSimple className="w-3 h-3 mr-1" /> Edit
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {activeTab === 'pages' && editingType && (
        <Card className="p-5 space-y-4" data-testid="legal-editor">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Editing: {editingType.replace('_', ' ').toUpperCase()}</h2>
            <Button size="sm" variant="ghost" onClick={() => setEditingType(null)}>Cancel</Button>
          </div>
          <Input label="Title" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} data-testid="legal-title-input" />
          <div className="space-y-1">
            <label className="block text-sm font-medium text-[#1A1F36]">Content</label>
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full px-3 py-2 border border-[#E0E0E0] rounded-md text-sm min-h-[300px] font-mono leading-relaxed"
              data-testid="legal-content-input"
            />
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={handleSave} disabled={saving} data-testid="save-legal-btn">
              {saving ? 'Saving...' : 'Save & Increment Version'}
            </Button>
            <p className="text-xs text-[#999]">Saving will increment the version and force all users to re-accept.</p>
          </div>
        </Card>
      )}

      {activeTab === 'history' && (
        history.length === 0 ? (
          <Card className="p-8 text-center text-[#888]">No acceptance records yet</Card>
        ) : (
          <Card className="divide-y divide-[#F0F0F0]" data-testid="acceptance-history">
            {history.map(h => (
              <div key={h.id} className="flex items-center gap-3 p-3 hover:bg-[#F8F9FB]" data-testid={`acceptance-row-${h.id}`}>
                <div className="w-8 h-8 rounded-full bg-[#059669]/10 flex items-center justify-center shrink-0">
                  <CheckCircle className="w-4 h-4 text-[#059669]" />
                </div>
                <div className="flex-1">
                  <span className="text-sm font-medium">{h.user_name || h.user_email}</span>
                  <Badge variant="default" className="ml-2">v{h.accepted_version}</Badge>
                </div>
                <span className="text-xs text-[#999]">{new Date(h.accepted_at).toLocaleString('en-US')}</span>
              </div>
            ))}
          </Card>
        )
      )}
    </div>
  );
}
